import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RepetitionInput = ({ 
  onRepetitionSubmit, 
  targetReps, 
  isLoading = false,
  currentSet,
  disabled = false 
}) => {
  const [inputValue, setInputValue] = useState('');
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    // Clear input when set changes
    setInputValue('');
    setFeedback(null);
  }, [currentSet]);

  const handleNumberClick = (number) => {
    if (disabled || isLoading) return;
    
    if (inputValue?.length < 3) { // Limit to 3 digits
      setInputValue(prev => prev + number?.toString());
    }
  };

  const handleClear = () => {
    if (disabled || isLoading) return;
    setInputValue('');
    setFeedback(null);
  };

  const handleBackspace = () => {
    if (disabled || isLoading) return;
    setInputValue(prev => prev?.slice(0, -1));
  };

  const handleSubmit = () => {
    if (disabled || isLoading || !inputValue) return;

    const reps = parseInt(inputValue);
    
    if (reps <= 0) {
      setFeedback({
        type: 'error',
        message: 'Please enter a valid number of repetitions'
      });
      return;
    }

    if (reps > 50) {
      setFeedback({
        type: 'warning',
        message: 'That seems like a lot! Are you sure?'
      });
      return;
    }

    // Provide feedback based on target
    let feedbackType = 'success';
    let feedbackMessage = 'Great job!';

    if (reps < targetReps - 2) {
      feedbackType = 'info';
      feedbackMessage = 'Good effort! We may adjust the intensity.';
    } else if (reps > targetReps + 2) {
      feedbackType = 'success';
      feedbackMessage = 'Excellent! You exceeded the target!';
    }

    setFeedback({
      type: feedbackType,
      message: feedbackMessage
    });

    // Submit after showing feedback
    setTimeout(() => {
      onRepetitionSubmit(reps);
      setInputValue('');
      setFeedback(null);
    }, 2000);
  };

  const numberButtons = [
    [1, 2, 3],
    [4, 5, 6],
    [7, 8, 9],
    ['clear', 0, 'backspace']
  ];

  const getFeedbackColor = (type) => {
    switch (type) {
      case 'success': return 'text-success bg-success/10 border-success/20';
      case 'error': return 'text-error bg-error/10 border-error/20';
      case 'warning': return 'text-warning bg-warning/10 border-warning/20';
      case 'info': return 'text-primary bg-primary/10 border-primary/20';
      default: return 'text-foreground bg-muted border-border';
    }
  };

  const getFeedbackIcon = (type) => {
    switch (type) {
      case 'success': return 'CheckCircle';
      case 'error': return 'AlertCircle';
      case 'warning': return 'AlertTriangle';
      case 'info': return 'Info';
      default: return 'MessageCircle';
    }
  };

  return (
    <div className="bg-surface rounded-2xl p-8 shadow-lg border border-border">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-foreground mb-2">
          Enter Repetitions
        </h2>
        <p className="text-xl text-muted-foreground">
          How many reps did you complete?
        </p>
      </div>
      {/* Input Display */}
      <div className="mb-8">
        <div className="bg-accent rounded-2xl p-8 text-center">
          <div className="text-6xl font-bold text-foreground mb-4 min-h-[80px] flex items-center justify-center">
            {inputValue || '0'}
          </div>
          <p className="text-xl text-muted-foreground">
            Target: {targetReps} reps
          </p>
        </div>
      </div>
      {/* Feedback Message */}
      {feedback && (
        <div className={`mb-8 p-6 rounded-xl border-2 ${getFeedbackColor(feedback?.type)}`}>
          <div className="flex items-center justify-center space-x-3">
            <Icon name={getFeedbackIcon(feedback?.type)} size={32} />
            <p className="text-xl font-semibold">{feedback?.message}</p>
          </div>
        </div>
      )}
      {/* Number Pad */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        {numberButtons?.flat()?.map((button, index) => {
          if (button === 'clear') {
            return (
              <Button
                key={index}
                variant="outline"
                size="xl"
                onClick={handleClear}
                disabled={disabled || isLoading}
                className="h-20 text-xl font-bold breathing-focus haptic-button"
              >
                <Icon name="RotateCcw" size={28} />
              </Button>
            );
          }
          
          if (button === 'backspace') {
            return (
              <Button
                key={index}
                variant="outline"
                size="xl"
                onClick={handleBackspace}
                disabled={disabled || isLoading}
                className="h-20 text-xl font-bold breathing-focus haptic-button"
              >
                <Icon name="Delete" size={28} />
              </Button>
            );
          }

          return (
            <Button
              key={index}
              variant="outline"
              size="xl"
              onClick={() => handleNumberClick(button)}
              disabled={disabled || isLoading}
              className="h-20 text-3xl font-bold breathing-focus haptic-button"
            >
              {button}
            </Button>
          );
        })}
      </div>
      {/* Submit Button */}
      <Button
        variant="default"
        size="xl"
        onClick={handleSubmit}
        disabled={!inputValue || disabled || isLoading}
        loading={isLoading}
        className="w-full h-20 text-2xl font-bold breathing-focus haptic-button"
      >
        {isLoading ? 'Processing...' : 'Complete Set'}
      </Button>
    </div>
  );
};

export default RepetitionInput;